import { ShieldCheck, Target, MapPin } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { useAudit } from "@/components/AuditContext";
import { IMAGES } from "@/lib/api";

const pillars = [
  {
    icon: ShieldCheck,
    label: "Notre parti pris",
    title: "La sécurité, jamais au rabais.",
    text: "Beaucoup de prestataires proposent des formules allégées, moins protégées, pour afficher un prix plus bas. Nous refusons ce compromis. Chez nous, chaque entreprise bénéficie exactement du même niveau de protection — le plus élevé. Votre sécurité n'est pas une variable d'ajustement.",
  },
  {
    icon: Target,
    label: "Notre spécialité",
    title: "Les petites entreprises, notre spécialité.",
    text: "Nous avons fait un choix rare : nous concentrer sur les petites entreprises. Cette spécialisation nous permet de parfaitement connaître vos contraintes, vos budgets et vos priorités. Que vous soyez seul ou une équipe d'une vingtaine de personnes, vous n'êtes jamais le petit dossier négligé au profit d'un plus gros client. Au-delà de vingt postes, parlons-en : nous adaptons la solution.",
  },
  {
    icon: MapPin,
    label: "Notre territoire",
    title: "Ancrés dans les Hauts-de-France.",
    text: "Nous intervenons exclusivement dans les Hauts-de-France, depuis Lille. Une présence locale, des interlocuteurs qui connaissent votre région et une vraie proximité — pas un centre d'appel à l'autre bout du monde.",
  },
];

export default function Pourquoi() {
  const { openAudit } = useAudit();
  return (
    <>
      <Seo title="Pourquoi choisir Umbo, spécialiste informatique TPE" description="Pourquoi choisir Umbo : la même protection pour tous, sans formule au rabais, et une spécialisation des petites entreprises des Hauts-de-France, jusqu'à 20 postes et au-delà." path="/pourquoi-umbo-cyber" />
      <section className="bg-navy grain relative pt-40 pb-24 md:pt-48 md:pb-32 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-[-8%] w-[42rem] h-[42rem] rounded-full bg-amber-500/10 blur-[150px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Pourquoi nous</span></Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-5 font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-white text-balance">
                Un prestataire qui vous ressemble.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed">
                Pas de discours technique, pas de promesses floues. Juste une manière simple et
                exigeante de prendre soin de l'informatique des petites entreprises.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-2xl">
              <img src={IMAGES.meeting} alt="Échange calme lors d'une réunion en petite entreprise" className="w-full h-[24rem] object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 md:py-32" data-testid="pourquoi-pillars">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 space-y-px">
          {pillars.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-slate-200">
                <div className="md:col-span-4">
                  <div className="w-12 h-12 rounded-lg bg-navy grid place-items-center mb-5"><p.icon className="w-6 h-6 text-amber-500" /></div>
                  <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">{p.label}</span>
                  <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-navy">{p.title}</h2>
                </div>
                <div className="md:col-span-8">
                  <p className="text-lg text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[50rem] h-[28rem] rounded-full bg-amber-500/12 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">
              Faisons connaissance, sans engagement.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <CTAButton size="lg" withArrow className="mt-10" onClick={openAudit} data-testid="pourquoi-audit-cta">
              Réserver un échange gratuit
            </CTAButton>
          </Reveal>
        </div>
      </section>
    </>
  );
}
